/**
 * Direct Cognito auth (no hosted UI redirect).
 * Uses InitiateAuth + RespondToAuthChallenge for login and forced password change.
 */

const COGNITO_REGION = "eu-north-1";
const CLIENT_ID = import.meta.env.VITE_COGNITO_CLIENT_ID as string;

const COGNITO_URL = `https://cognito-idp.${COGNITO_REGION}.amazonaws.com/`;

interface AuthResult {
  type: "authenticated";
  accessToken: string;
  idToken: string;
  refreshToken: string;
  expiresIn: number;
}

interface ChallengeResult {
  type: "new_password_required";
  session: string;
  email: string;
}

interface MfaSetupRequired {
  type: "mfa_setup_required";
  session: string;
  email: string;
}

interface MfaChallengeRequired {
  type: "software_token_mfa";
  session: string;
  email: string;
}

export type LoginResult = AuthResult | ChallengeResult | MfaSetupRequired | MfaChallengeRequired;

async function cognitoRequest(action: string, payload: Record<string, unknown>) {
  const res = await fetch(COGNITO_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-amz-json-1.1",
      "X-Amz-Target": `AWSCognitoIdentityProviderService.${action}`,
    },
    body: JSON.stringify(payload),
  });

  const body = await res.json();

  if (!res.ok) {
    const code = body.__type?.split("#").pop() ?? "UnknownError";
    const message = body.message ?? body.Message ?? "Authentication failed";
    const err = new Error(message);
    (err as unknown as Record<string, unknown>).code = code;
    throw err;
  }

  return body;
}

export async function login(email: string, password: string): Promise<LoginResult> {
  const result = await cognitoRequest("InitiateAuth", {
    AuthFlow: "USER_PASSWORD_AUTH",
    ClientId: CLIENT_ID,
    AuthParameters: {
      USERNAME: email,
      PASSWORD: password,
    },
  });

  if (result.ChallengeName === "NEW_PASSWORD_REQUIRED") {
    return {
      type: "new_password_required",
      session: result.Session,
      email,
    };
  }

  if (result.ChallengeName === "MFA_SETUP") {
    return {
      type: "mfa_setup_required",
      session: result.Session,
      email,
    };
  }

  if (result.ChallengeName === "SOFTWARE_TOKEN_MFA") {
    return {
      type: "software_token_mfa",
      session: result.Session,
      email,
    };
  }

  if (result.ChallengeName) {
    throw new Error(`Unsupported authentication challenge: ${result.ChallengeName}`);
  }

  if (!result.AuthenticationResult) {
    throw new Error("Authentication failed: no tokens returned");
  }

  return {
    type: "authenticated",
    accessToken: result.AuthenticationResult.AccessToken,
    idToken: result.AuthenticationResult.IdToken,
    refreshToken: result.AuthenticationResult.RefreshToken,
    expiresIn: result.AuthenticationResult.ExpiresIn,
  };
}

export async function completeNewPassword(
  email: string,
  newPassword: string,
  session: string,
): Promise<LoginResult> {
  const result = await cognitoRequest("RespondToAuthChallenge", {
    ChallengeName: "NEW_PASSWORD_REQUIRED",
    ClientId: CLIENT_ID,
    ChallengeResponses: {
      USERNAME: email,
      NEW_PASSWORD: newPassword,
    },
    Session: session,
  });

  if (result.ChallengeName === "MFA_SETUP") {
    return { type: "mfa_setup_required", session: result.Session, email };
  }

  if (result.ChallengeName === "SOFTWARE_TOKEN_MFA") {
    return { type: "software_token_mfa", session: result.Session, email };
  }

  if (result.ChallengeName) {
    throw new Error(`Unsupported authentication challenge: ${result.ChallengeName}`);
  }

  if (!result.AuthenticationResult) {
    throw new Error("Authentication failed: no tokens returned");
  }

  return {
    type: "authenticated",
    accessToken: result.AuthenticationResult.AccessToken,
    idToken: result.AuthenticationResult.IdToken,
    refreshToken: result.AuthenticationResult.RefreshToken,
    expiresIn: result.AuthenticationResult.ExpiresIn,
  };
}

export async function changePassword(
  accessToken: string,
  previousPassword: string,
  proposedPassword: string,
): Promise<void> {
  await cognitoRequest("ChangePassword", {
    AccessToken: accessToken,
    PreviousPassword: previousPassword,
    ProposedPassword: proposedPassword,
  });
}

export async function forgotPassword(email: string): Promise<void> {
  await cognitoRequest("ForgotPassword", {
    ClientId: CLIENT_ID,
    Username: email,
  });
}

export async function confirmForgotPassword(
  email: string,
  code: string,
  newPassword: string,
): Promise<void> {
  await cognitoRequest("ConfirmForgotPassword", {
    ClientId: CLIENT_ID,
    Username: email,
    ConfirmationCode: code,
    Password: newPassword,
  });
}

export async function cognitoRefreshTokens(refreshToken: string): Promise<AuthResult> {
  const result = await cognitoRequest("InitiateAuth", {
    AuthFlow: "REFRESH_TOKEN_AUTH",
    ClientId: CLIENT_ID,
    AuthParameters: {
      REFRESH_TOKEN: refreshToken,
    },
  });

  if (!result.AuthenticationResult) {
    throw new Error("Token refresh failed: no tokens returned");
  }

  return {
    type: "authenticated",
    accessToken: result.AuthenticationResult.AccessToken,
    idToken: result.AuthenticationResult.IdToken,
    refreshToken: result.AuthenticationResult.RefreshToken ?? refreshToken,
    expiresIn: result.AuthenticationResult.ExpiresIn,
  };
}

export async function associateSoftwareToken(
  session: string,
): Promise<{ secretCode: string; session: string }> {
  const result = await cognitoRequest("AssociateSoftwareToken", {
    Session: session,
  });
  return {
    secretCode: result.SecretCode,
    session: result.Session,
  };
}

export async function verifySoftwareToken(
  email: string,
  session: string,
  userCode: string,
): Promise<AuthResult> {
  const result = await cognitoRequest("VerifySoftwareToken", {
    Session: session,
    UserCode: userCode,
    FriendlyDeviceName: "Authenticator",
  });

  if (result.Status === "SUCCESS" && result.Session) {
    const authResult = await cognitoRequest("RespondToAuthChallenge", {
      ChallengeName: "MFA_SETUP",
      ClientId: CLIENT_ID,
      Session: result.Session,
      ChallengeResponses: {
        USERNAME: email,
      },
    });
    if (!authResult.AuthenticationResult) {
      throw new Error("MFA setup failed: no tokens returned");
    }
    return {
      type: "authenticated",
      accessToken: authResult.AuthenticationResult.AccessToken,
      idToken: authResult.AuthenticationResult.IdToken,
      refreshToken: authResult.AuthenticationResult.RefreshToken,
      expiresIn: authResult.AuthenticationResult.ExpiresIn,
    };
  }

  if (!result.AuthenticationResult) {
    throw new Error("MFA verification failed: no tokens returned");
  }

  return {
    type: "authenticated",
    accessToken: result.AuthenticationResult.AccessToken,
    idToken: result.AuthenticationResult.IdToken,
    refreshToken: result.AuthenticationResult.RefreshToken,
    expiresIn: result.AuthenticationResult.ExpiresIn,
  };
}

export async function respondToMfaChallenge(
  email: string,
  mfaCode: string,
  session: string,
): Promise<AuthResult> {
  const result = await cognitoRequest("RespondToAuthChallenge", {
    ChallengeName: "SOFTWARE_TOKEN_MFA",
    ClientId: CLIENT_ID,
    ChallengeResponses: {
      USERNAME: email,
      SOFTWARE_TOKEN_MFA_CODE: mfaCode,
    },
    Session: session,
  });

  if (!result.AuthenticationResult) {
    throw new Error("MFA verification failed: no tokens returned");
  }

  return {
    type: "authenticated",
    accessToken: result.AuthenticationResult.AccessToken,
    idToken: result.AuthenticationResult.IdToken,
    refreshToken: result.AuthenticationResult.RefreshToken,
    expiresIn: result.AuthenticationResult.ExpiresIn,
  };
}
